/**
 * @fileoverview PlanValidationEngine
 * Engine for comparing plan processes vs order requirements
 * and calculating process coverage
 */

import { OrderDataAnalysisService } from './OrderDataAnalysisService';
import { ProcessMappingService } from './ProcessMappingService';
import { pb } from '../helpers/pocketbase_helper';

/**
 * Engine for validating plan coverage against order requirements
 */
export class PlanValidationEngine {
  /**
   * Validates if a plan has all required processes for an order
   * @param {string} orderId - ID of the production order
   * @param {string} planId - ID of the plan to validate
   * @returns {Promise<Object>} Validation result with coverage details
   */
  static async validatePlanForOrder(orderId, planId) {
    if (!orderId || !planId) {
      throw new Error('ID de orden y plan son requeridos');
    }

    try {
      // Get order data and required processes
      const orderAnalysis = await OrderDataAnalysisService.analyzeOrderData(orderId);
      const requiredProcesses = await ProcessMappingService.getRequiredProcesses(
        orderAnalysis.internalRelationships
      );

      // Get plan data and available processes
      const plan = await this.getPlanWithProcesses(planId);
      const planProcesses = this.extractPlanProcesses(plan);
      const availableProcessTables = planProcesses.processTables; // Table names for comparison
      const availableProcessNames = planProcesses.processNames; // Process names for display

      // Convert available process tables to process names for comparison
      const availableProcessNamesForComparison = await this.convertTableNamesToProcessNames(availableProcessTables);

      // Calculate coverage
      const coverage = this.calculateProcessCoverage(requiredProcesses, availableProcessNamesForComparison);

      const result = {
        isValid: coverage.isValid,
        orderId,
        planId,
        requiredProcesses, // These are already process names from ProcessMappingService
        availableProcesses: availableProcessNames, // Use process names for display
        missingProcesses: coverage.missingProcesses, // These are process names from comparison
        coveragePercentage: coverage.coveragePercentage
      };
      
      return result;
    } catch (error) {
      throw new Error(`Error al validar plan para orden: ${error.message}`);
    }
  }

  /**
   * Gets plan data with expanded process information
   * @param {string} planId - ID of the plan
   * @returns {Promise<Object>} Plan data with processes
   */
  static async getPlanWithProcesses(planId) {
    try {
      return await pb.collection('plan_centro_costo').getOne(planId, {
        expand: 'centro_costo_peso.centro_costo.proceso'
      });
    } catch (error) {
      throw new Error(`Error al obtener plan: ${error.message}`);
    }
  }

  /**
   * Extracts process information from plan centro_costo_peso
   * @param {Object} plan - Plan data with expanded centro_costo_peso
   * @returns {Object} Object with process tables and names
   */
  static extractPlanProcesses(plan) {
    // Check if we have expanded centro_costo_peso data
    const expandedCentroCostoPeso = plan.expand?.centro_costo_peso;
    if (!expandedCentroCostoPeso || !Array.isArray(expandedCentroCostoPeso)) {
      return { processTables: [], processNames: [] };
    }

    const processTables = [];
    const processNames = [];

    expandedCentroCostoPeso.forEach((centroPeso) => {
      // Check if it's expanded data
      if (centroPeso.expand?.centro_costo?.expand?.proceso?.tabla) {
        const processTable = centroPeso.expand.centro_costo.expand.proceso.tabla;
        const processName = centroPeso.expand.centro_costo.expand.proceso.nombre;
        processTables.push(processTable); // For comparison
        processNames.push(processName); // For display
      }
    });

    return { processTables, processNames };
  }

  /**
   * Converts table names to process names using the mapping service
   * @param {Array<string>} tableNames - Array of table names
   * @returns {Promise<Array<string>>} Array of process names
   */
  static async convertTableNamesToProcessNames(tableNames) {
    if (!tableNames || tableNames.length === 0) {
      return [];
    }

    try {
      const mappings = await ProcessMappingService.getProcessMappings();
      
      const mappingLookup = new Map();
      mappings.forEach(mapping => {
        mappingLookup.set(mapping.tabla, mapping.nombre);
      });
      
      const processNames = tableNames.map(tableName => {
        const processName = mappingLookup.get(tableName);
        return processName;
      }).filter(name => name !== null && name !== undefined);
      
      return processNames;
    } catch (error) {
      return [];
    }
  }

  /**
   * Calculates process coverage between required and available processes
   * @param {Array<string>} requiredProcesses - Required process names
   * @param {Array<string>} availableProcesses - Available process names
   * @returns {Object} Coverage calculation result
   */
  static calculateProcessCoverage(requiredProcesses, availableProcesses) {
    if (!requiredProcesses || requiredProcesses.length === 0) {
      return {
        isValid: true,
        missingProcesses: [],
        coveragePercentage: 100
      };
    }

    const availableSet = new Set(availableProcesses || []);
    const missingProcesses = requiredProcesses.filter(process => !availableSet.has(process));
    
    const coveragePercentage = Math.round(
      ((requiredProcesses.length - missingProcesses.length) / requiredProcesses.length) * 100
    );

    return {
      isValid: missingProcesses.length === 0,
      missingProcesses,
      coveragePercentage
    };
  }

  /**
   * Gets a summary of validation results for UI display
   * @param {Object} validationResult - Result from validatePlanForOrder
   * @returns {Object} Summary for UI display
   */
  static getValidationSummary(validationResult) {
    if (validationResult.isValid) {
      return {
        status: 'valid',
        message: `Plan válido - ${validationResult.coveragePercentage}% de cobertura de procesos`,
        canProceed: true
      };
    } else {
      const missingList = validationResult.missingProcesses.join(', ');
      return {
        status: 'invalid',
        message: `Plan incompleto - ${validationResult.coveragePercentage}% de cobertura. Faltan procesos: ${missingList}`,
        canProceed: false
      };
    }
  }

  /**
   * Validates multiple plans against an order and returns the best match
   * @param {string} orderId - ID of the production order
   * @param {Array<string>} planIds - Array of plan IDs to validate
   * @returns {Promise<Object>} Best plan match with validation details
   */
  static async findBestPlanMatch(orderId, planIds) {
    if (!orderId || !planIds || planIds.length === 0) {
      throw new Error('ID de orden y lista de planes son requeridos');
    }

    try {
      const validations = await Promise.all(
        planIds.map(planId => this.validatePlanForOrder(orderId, planId))
      );

      // Sort by coverage percentage (descending) and then by validity
      const sortedValidations = validations.sort((a, b) => {
        if (a.isValid !== b.isValid) {
          return b.isValid - a.isValid; // Valid plans first
        }
        return b.coveragePercentage - a.coveragePercentage; // Higher coverage first
      });

      const bestMatch = sortedValidations[0];

      return {
        bestPlan: bestMatch,
        allValidations: validations,
        recommendations: this.getPlanRecommendations(sortedValidations)
      };
    } catch (error) {
      throw new Error(`Error al encontrar mejor plan: ${error.message}`);
    }
  }

  /**
   * Gets recommendations for plan selection
   * @param {Array<Object>} validations - Sorted validation results
   * @returns {Object} Recommendations for plan selection
   */
  static getPlanRecommendations(validations) {
    const validPlans = validations.filter(v => v.isValid);
    const invalidPlans = validations.filter(v => !v.isValid);

    return {
      validPlans: validPlans.length,
      invalidPlans: invalidPlans.length,
      bestCoverage: validations[0]?.coveragePercentage || 0,
      suggestions: this.generateSuggestions(validations)
    };
  }

  /**
   * Generates suggestions based on validation results
   * @param {Array<Object>} validations - Validation results
   * @returns {Array<string>} Array of suggestion messages
   */
  static generateSuggestions(validations) {
    const suggestions = [];

    const validPlans = validations.filter(v => v.isValid);
    const invalidPlans = validations.filter(v => !v.isValid);

    if (validPlans.length === 0) {
      suggestions.push('No hay planes que cubran todos los procesos requeridos');
      suggestions.push('Considere crear un nuevo plan o modificar uno existente');
    } else if (validPlans.length === 1) {
      suggestions.push('Solo hay un plan válido disponible');
    } else {
      suggestions.push(`Hay ${validPlans.length} planes válidos disponibles`);
    }

    if (invalidPlans.length > 0) {
      const avgCoverage = invalidPlans.reduce((sum, v) => sum + v.coveragePercentage, 0) / invalidPlans.length;
      suggestions.push(`Planes incompletos tienen un promedio de ${Math.round(avgCoverage)}% de cobertura`);
    }

    return suggestions;
  }
}
